/**
 * Sidebar — Collapsible left-navigation for Compliance Hub.
 * ui-review-phase2 §2 — Primary Navigation Model.
 *
 * Expanded:  200px (icon + label)
 * Collapsed: 52px  (icon only, tooltips on hover)
 * State persisted in localStorage under 'ch-sidebar-collapsed'.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RESEARCH_BRIEF_UI_ENABLED } from '../constants';

/* ── Types ───────────────────────────────────────────────────── */
interface SidebarProps {
  isAdminUser: boolean;
  /** Whether settings panel is open (so Settings sidebar item can reflect it) */
  onOpenSettings: () => void;
  /** Called for Restart Tour action (via Help dropdown) */
  onOpenHelp: () => void;
  onOpenResources: () => void;
  /** Open session history sidebar */
  onOpenHistory: () => void;
  /** Open keyboard shortcuts modal */
  onOpenShortcuts: () => void;
  /** Bookmark + highlight count badge */
  researchBadge?: number;
  /** Unread corpus updates */
  hasCorpusUpdates?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  emoji: string;
  route?: string;
  action?: 'settings' | 'help' | 'resources' | 'history';
  adminOnly?: boolean;
  badge?: number;
  dot?: boolean;
  isSection?: boolean;
  isBottom?: boolean;
  /** Shows a SOON badge and muted preview style */
  soon?: boolean;
  /** Optional tag label shown next to section headers */
  sectionTag?: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'chat',           label: 'Query',          emoji: '💬', route: '/' },
  { id: 'research',       label: 'Research',       emoji: '📓', route: '/research' },
  { id: 'history',        label: 'History',        emoji: '🕐', action: 'history' },
  { id: 'tools-header',   label: 'Tools',          emoji: '🔧', isSection: true },
  { id: 'corpus-browser',    label: 'Corpus Browser',    emoji: '📚', route: '/tools/corpus-browser' },
  // F50 (Grant directive 2026-07-13): only verified surfaces ship live. These
  // four are code-complete but paused as Coming Soon placeholders until each
  // passes its Phase D verification batch.
  { id: 'report-builder',    label: 'Report Builder',    emoji: '📄', route: '/portal/coming-soon/report-builder',   soon: true },
  { id: 'copy-builder',      label: 'Copy Builder',      emoji: '✍️',  route: '/portal/coming-soon/copy-builder',     soon: true },
  { id: 'manual-review',     label: 'Manual Review',     emoji: '🔍', route: '/portal/coming-soon/manual-review',    soon: true },
  { id: 'compliance-files',  label: 'Compliance Files',  emoji: '📁', route: '/portal/coming-soon/compliance-files', soon: true },
  { id: 'coming-soon-header',   label: 'Coming in v1.1',    emoji: '',    isSection: true, sectionTag: 'POST-MIGRATION' },
  { id: 'product-knowledge',    label: 'Product Knowledge', emoji: '🧠', route: '/portal/coming-soon/product-knowledge', soon: true },
  { id: 'compliance-forms-soon',label: 'Compliance Forms',  emoji: '📋', route: '/portal/coming-soon/compliance-forms',  soon: true },
  { id: 'abi-integrations',     label: 'ABI Integrations',  emoji: '🔌', route: '/portal/coming-soon/abi-integrations',  soon: true },
  { id: 'wels-sync',            label: 'WELS Auto-sync',    emoji: '💧', route: '/portal/coming-soon/wels-sync',          soon: true },
  { id: 'resources',      label: 'Resources',      emoji: '🔗', action: 'resources' },
  { id: 'settings',       label: 'Settings',       emoji: '⚙️',  action: 'settings' },
  // No pinned bottom section: a reserved bottom nav forced the main list into
  // a scroll region even when the window could fit everything. One flat list —
  // the scrollbar appears only on genuine overflow.
  { id: 'help',           label: 'Help',           emoji: '❓', action: 'help' },
  { id: 'portal',         label: 'Admin Portal',   emoji: '⚡',  route: '/portal' },
];

const LS_COLLAPSED_KEY = 'ch-sidebar-collapsed';

/* ── Chevron icon ────────────────────────────────────────────── */
function ChevronIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s ease',
        flexShrink: 0,
      }}
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/* ── Main component ──────────────────────────────────────────── */
export default function Sidebar({
  isAdminUser,
  onOpenSettings,
  onOpenHelp,
  onOpenResources,
  onOpenHistory,
  onOpenShortcuts,
  researchBadge = 0,
  hasCorpusUpdates = false,
}: SidebarProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LS_COLLAPSED_KEY) === 'true';
    } catch { return false; }
  });

  const [showHelpDropdown, setShowHelpDropdown] = useState(false);
  const helpContainerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist collapsed state
  useEffect(() => {
    try {
      localStorage.setItem(LS_COLLAPSED_KEY, String(collapsed));
      // Update CSS variable on root so layout can react
      document.documentElement.style.setProperty(
        '--sidebar-width',
        collapsed ? '52px' : '200px',
      );
    } catch { /* ignore */ }
  }, [collapsed]);

  // Set initial CSS variable on mount
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      collapsed ? '52px' : '200px',
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close help dropdown on click-outside or Escape
  useEffect(() => {
    if (!showHelpDropdown) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (helpContainerRef.current && !helpContainerRef.current.contains(e.target as Node)) {
        setShowHelpDropdown(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowHelpDropdown(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showHelpDropdown]);

  const toggle = useCallback(() => setCollapsed((v) => !v), []);

  const isActive = useCallback(
    (item: NavItem) => {
      if (!item.route) return false;
      if (item.route === '/') {
        // Query is active when on root and not on any tool/research route
        return (
          location.pathname === '/' ||
          location.pathname === '' ||
          location.pathname.startsWith('/q/')
        );
      }
      return location.pathname.startsWith(item.route);
    },
    [location.pathname],
  );

  const handleClick = useCallback(
    (item: NavItem) => {
      if (item.isSection) return;
      if (item.route) {
        navigate(item.route);
      } else if (item.action === 'settings') {
        onOpenSettings();
      } else if (item.action === 'history') {
        onOpenHistory();
      } else if (item.action === 'help') {
        setShowHelpDropdown((v) => !v);
      } else if (item.action === 'resources') {
        onOpenResources();
      }
    },
    [navigate, onOpenSettings, onOpenHelp, onOpenResources],
  );

  const topItems = NAV_ITEMS;

  const renderItem = (item: NavItem) => {
    if (item.adminOnly && !isAdminUser) return null;
    // F46: Research Brief is not part of the product (Grant directive 2026-07-13)
    if (item.id === 'research' && !RESEARCH_BRIEF_UI_ENABLED) return null;
    if (item.isSection) {
      return (
        <div key={item.id} className="sidebar-section-header">
          {collapsed ? (
            <span className="sidebar-section-divider" title={item.label} />
          ) : (
            <span className="sidebar-section-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {item.label}
              {item.sectionTag && (
                <span style={{ fontSize: 9, fontWeight: 700, color: '#7c6af7', background: 'rgba(124,106,247,0.15)', padding: '1px 5px', borderRadius: 8, letterSpacing: '0.04em', flexShrink: 0 }}>
                  {item.sectionTag}
                </span>
              )}
            </span>
          )}
        </div>
      );
    }

    // Help item — special: shows internal dropdown
    if (item.id === 'help') {
      return (
        <div key={item.id} ref={helpContainerRef} style={{ position: 'relative' }}>
          <button
            className={`sidebar-item${collapsed ? ' sidebar-item--collapsed' : ''}`}
            onClick={() => handleClick(item)}
            title={collapsed ? item.label : undefined}
            aria-label={item.label}
            aria-expanded={showHelpDropdown}
            data-tour="help-tour"
          >
            <span className="sidebar-item-icon" aria-hidden>{item.emoji}</span>
            {!collapsed && <span className="sidebar-item-label">{item.label}</span>}
          </button>
          {showHelpDropdown && (
            <div
              className="sidebar-help-dropdown"
              style={{
                position: 'absolute',
                bottom: 0,
                left: collapsed ? '60px' : 'calc(100% + 8px)',
                background: 'var(--bg-2, var(--bg-card, #1e2130))',
                border: '1px solid var(--line, rgba(255,255,255,0.1))',
                borderRadius: '8px',
                padding: '4px',
                minWidth: '180px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                zIndex: 300,
                whiteSpace: 'nowrap',
              }}
            >
              <button
                className="sidebar-help-item"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', borderRadius: '6px', background: 'none', border: 'none', color: 'inherit', fontSize: '13px', width: '100%', cursor: 'pointer', textAlign: 'left' }}
                onClick={() => { onOpenShortcuts(); setShowHelpDropdown(false); }}
              >
                ⌨️ Keyboard Shortcuts
              </button>
              <button
                className="sidebar-help-item"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', borderRadius: '6px', background: 'none', border: 'none', color: 'inherit', fontSize: '13px', width: '100%', cursor: 'pointer', textAlign: 'left' }}
                onClick={() => { onOpenHelp(); setShowHelpDropdown(false); }}
              >
                🚀 Restart Tour
              </button>
            </div>
          )}
        </div>
      );
    }

    const active = isActive(item);
    const badge = item.id === 'research' ? researchBadge : (item.badge ?? 0);
    const dot = item.id === 'corpus-browser' ? hasCorpusUpdates : (item.dot ?? false);

    return (
      <button
        key={item.id}
        className={`sidebar-item${active ? ' sidebar-item--active' : ''}${collapsed ? ' sidebar-item--collapsed' : ''}${item.soon ? ' sidebar-item--coming-soon' : ''}`}
        onClick={() => handleClick(item)}
        title={collapsed ? item.label : undefined}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
      >
        <span className="sidebar-item-icon" aria-hidden>
          {item.emoji}
        </span>
        {!collapsed && (
          <span className="sidebar-item-label">{item.label}</span>
        )}
        {item.soon && !collapsed && (
          <span className="sidebar-soon-badge">SOON</span>
        )}
        {badge > 0 && (
          <span className="sidebar-badge" aria-label={`${badge} items`}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        {dot && !badge && (
          <span className="sidebar-dot" aria-label="New updates" />
        )}
      </button>
    );
  };

  return (
    <aside
      className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`}
      aria-label="Primary navigation"
      data-tour="sidebar-nav"
    >
      {/* Collapse toggle */}
      <button
        className="sidebar-toggle"
        onClick={toggle}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
      >
        <ChevronIcon collapsed={collapsed} />
        {!collapsed && <span className="sidebar-toggle-label">Collapse</span>}
      </button>

      {/* Single flat nav — scrollbar only appears on genuine overflow */}
      <nav className="sidebar-nav" aria-label="Main navigation">
        {topItems.map(renderItem)}
      </nav>
    </aside>
  );
}
